import { RichTextEditor } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import TextAlign from "@tiptap/extension-text-align";
import { useEffect } from "react";
import { Group, Menu, ScrollArea, UnstyledButton, createStyles } from "@mantine/core";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Superscript from "@tiptap/extension-superscript";
import Highlight from "@tiptap/extension-highlight";
import Subscript from "@tiptap/extension-subscript";
import Image from "@tiptap/extension-image";
import { Dropzone, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { Icon } from "./icons";
import { faCaretDown, faImage } from "@fortawesome/pro-duotone-svg-icons";
import StarterKit from "@tiptap/starter-kit";
import { FontSize } from "./extensions/font-size";
import TextStyle from "@tiptap/extension-text-style";

export interface RichTextProps {
  content: string;
  onChange: (content: string) => unknown;
}

const fontSizes = (() => {
  const sizes: number[] = [];
  for (let i = 8; i <= 20; i++) {
    sizes.push(i);
  }
  for (let i = 22; i <= 40; i = i + 2) {
    sizes.push(i);
  }
  for (let i = 44; i <= 240; i = i + 4) {
    sizes.push(i);
  }
  return sizes;
})();

const useStyles = createStyles((theme) => ({
  fontSizeControl: {
    fontSize: "14px",
    gap: theme.other.arc.margins[1],
    padding: theme.other.arc.paddings[1],
  },
  imageControl: {
    border: "none",
    padding: 0,
  },
}));

export const RichText = ({ content, onChange }: RichTextProps) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline,
      Superscript,
      Subscript,
      Highlight,
      Image.configure({
        allowBase64: true,
        inline: true,
      }),
      TextStyle,
      FontSize,
    ],
    content,
    onUpdate: ({ editor: local }) => {
      onChange?.(local.getHTML());
    },
  });
  useEffect(() => {
    if (editor?.getHTML() !== content) {
      editor?.commands.setContent(content);
    }
  }, [content]);

  let fontSize = 12;
  if (editor?.isActive("textStyle")) {
    const { fontSize: val3 } = editor?.getAttributes("textStyle") ?? {};
    fontSize = Number(val3 != null ? val3?.replace("px", "") : 12);
  }

  const onFontSizeChange = (size: number) => {
    return () => {
      editor?.chain().focus().setFontSize(`${size}`).run();
    };
  };

  const onImageSelect = (files: File[]) => {
    const reader = new FileReader();
    reader.readAsDataURL(files[0]);
    reader.onload = () => {
      editor
        ?.chain()
        .focus()
        .setImage({ src: reader.result as string })
        .run();
    };
  };

  return (
    <RichTextEditor editor={editor}>
      <RichTextEditor.Toolbar sticky stickyOffset={60}>
        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Bold />
          <RichTextEditor.Italic />
          <RichTextEditor.Underline />
          <RichTextEditor.Strikethrough />
          <RichTextEditor.ClearFormatting />
          <RichTextEditor.Highlight />
          <RichTextEditor.Code />
          <FontSizeControl fontSize={fontSize} active={editor?.isActive("textStyle") ?? false} onClick={onFontSizeChange} />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.H1 />
          <RichTextEditor.H2 />
          <RichTextEditor.H3 />
          <RichTextEditor.H4 />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Blockquote />
          <RichTextEditor.Hr />
          <RichTextEditor.BulletList />
          <RichTextEditor.OrderedList />
          <RichTextEditor.Subscript />
          <RichTextEditor.Superscript />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.Link />
          <RichTextEditor.Unlink />
          <ImageControl onSelect={onImageSelect} active={editor?.isActive("image") ?? false} />
        </RichTextEditor.ControlsGroup>

        <RichTextEditor.ControlsGroup>
          <RichTextEditor.AlignLeft />
          <RichTextEditor.AlignCenter />
          <RichTextEditor.AlignJustify />
          <RichTextEditor.AlignRight />
        </RichTextEditor.ControlsGroup>
      </RichTextEditor.Toolbar>

      <ScrollArea style={{ height: "calc(100vh - 360px)" }} offsetScrollbars>
        <RichTextEditor.Content />
      </ScrollArea>
    </RichTextEditor>
  );
};

const FontSizeControl = ({ fontSize, onClick, active = false }: { fontSize: number; onClick: (size: number) => () => void; active: boolean }) => {
  const { classes } = useStyles();
  return (
    <RichTextEditor.Control interactive={true} active={active}>
      <Menu>
        <Menu.Target>
          <UnstyledButton title="Font size">
            <Group className={classes.fontSizeControl}>
              {fontSize}
              <Icon icon={faCaretDown} />
            </Group>
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown p={0}>
          <ScrollArea style={{ height: "150px" }}>
            {fontSizes.map((bt) => {
              return (
                <Menu.Item key={bt} onClick={onClick(bt)}>
                  {bt}
                </Menu.Item>
              );
            })}
          </ScrollArea>
        </Menu.Dropdown>
      </Menu>
    </RichTextEditor.Control>
  );
};

const ImageControl = ({ onSelect, active = false }: { onSelect: (files: File[]) => void; active: boolean }) => {
  const { classes } = useStyles();
  return (
    <Dropzone title="Image" className={classes.imageControl} multiple={false} onDrop={onSelect} maxSize={3 * 1024 ** 2} accept={IMAGE_MIME_TYPE}>
      <RichTextEditor.Control interactive={true} active={active}>
        <Icon icon={faImage} />
      </RichTextEditor.Control>
    </Dropzone>
  );
};
